import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Table,
  Space,
  Tooltip,
  Button,
  PageHeader,
  Input,
  Switch,
  Tag,
} from 'antd';
import {
  EditOutlined,
  DeleteOutlined,
  PlusCircleOutlined,
  KeyOutlined,
  CopyOutlined,
} from '@ant-design/icons';

import {
  handleApiSuccess,
  handleApiError,
  MyBreadcrumb,
  confirmDelete,
  accessControls,
} from 'utils';
import {
  apiKeysListService,
  apiKeysDeleteService,
  apiKeysToggleActiveService,
} from 'services/apikeys';

import { debounce } from 'lodash';
import { message } from 'antd';

const { Search } = Input;

export default function ApiKeysList() {
  const history = useHistory();
  const [isLoading, setIsLoading] = useState(false);
  const [dataSource, setDataSource] = useState(null);
  const [permissions, setPermissions] = useState([]);

  const columns = [
    {
      title: 'ID',
      dataIndex: '_id',
      key: '_id',
      className: 'hide',
      sorter: function (a, b) {
        return a._id.localeCompare(b._id);
      },
    },
    {
      title: 'Key Name',
      dataIndex: 'key_name',
      key: 'key_name',
      sorter: function (a, b) {
        return (a.key_name || '').localeCompare(b.key_name || '');
      },
    },
    {
      title: 'User',
      dataIndex: 'user',
      key: 'user',
      render: (user) => (
        <div>
          <div>
            <strong>{user?.display_name || 'N/A'}</strong>
          </div>
          <div style={{ color: '#666', fontSize: '12px' }}>
            {user?.email || 'N/A'}
          </div>
          <div style={{ color: '#666', fontSize: '12px' }}>
            Role: {user?.role || 'N/A'}
          </div>
        </div>
      ),
      sorter: function (a, b) {
        return (a.user?.display_name || '').localeCompare(
          b.user?.display_name || '',
        );
      },
    },
    {
      title: 'API Key',
      dataIndex: 'apikey',
      key: 'apikey',
      render: (text) => (
        <Space>
          <span>{text}</span>
          <Button
            icon={<CopyOutlined />}
            size="small"
            onClick={() => {
              navigator.clipboard.writeText(text);
              message.success('API Key copied to clipboard');
            }}
          />
        </Space>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (active, record) => (
        <Switch
          checked={active}
          onChange={(checked) => toggleApiKeyStatus(record._id, checked)}
          disabled={!accessControls.can(permissions, 'ApiKeys.update:any')}
        />
      ),
    },
    {
      title: 'Created At',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (text) => new Date(text).toLocaleString(),
    },
    {
      title: 'Action',
      key: 'action',
      render: (text, record) => (
        <Space size="middle">
          {accessControls.can(permissions, 'Client.update:any') && (
            <Tooltip title="Edit">
              <Button
                onClick={() => history.push(`/apikeys/update/${record._id}`)}
                shape="circle"
                icon={<EditOutlined />}
              />
            </Tooltip>
          )}
          {accessControls.can(permissions, 'Client.delete:any') && (
            <Tooltip title="Delete">
              <Button
                onClick={() => deleteRecord(record._id)}
                shape="circle"
                icon={<DeleteOutlined />}
              />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  useEffect(() => {
    loadData();
    loadPermissions();
  }, []);

  const loadPermissions = () => {
    accessControls.fetch().then((permissions) => {
      console.log(permissions);
      setPermissions(permissions);
    });
  };

  const loadData = () => {
    setIsLoading(true);
    apiKeysListService()
      .then((response) => {
        setIsLoading(false);
        if (response.data.items && response.data.items.length > 0) {
          setDataSource(response.data.items);
        } else {
          setDataSource([]);
        }
      })
      .catch((error) => {
        setIsLoading(false);
        handleApiError(error);
      });
  };

  const deleteRecord = (id) => {
    confirmDelete().then(() => {
      setIsLoading(true);
      apiKeysDeleteService(id)
        .then((response) => {
          setIsLoading(false);
          handleApiSuccess(response);
          loadData();
        })
        .catch((error) => {
          setIsLoading(false);
          handleApiError(error);
        });
    });
  };

  const toggleApiKeyStatus = (id, isActive) => {
    setIsLoading(true);
    apiKeysToggleActiveService(id, isActive)
      .then((response) => {
        setIsLoading(false);
        handleApiSuccess(response);
        loadData();
      })
      .catch((error) => {
        setIsLoading(false);
        handleApiError(error);
      });
  };

  return (
    <div>
      <MyBreadcrumb items={[{ label: 'API Keys', path: '' }]} />
      <PageHeader
        ghost={false}
        avatar={{ icon: <KeyOutlined /> }}
        title="API Keys"
        subTitle="Manage API access keys"
        extra={
          accessControls.can(permissions, 'Client.create:any')
            ? [
                <Button
                  shape="round"
                  onClick={() => history.push('/apikeys/create')}
                  type="primary"
                  icon={<PlusCircleOutlined />}
                >
                  Generate New Key
                </Button>,
              ]
            : []
        }
      ></PageHeader>
      <Table
        dataSource={dataSource}
        columns={columns}
        loading={isLoading}
        rowKey="_id"
      />
    </div>
  );
}
